import { Atom, atom, PrimitiveAtom, WritableAtom } from "jotai";
import { atomFamily } from "jotai/utils";
import { Disclosure, FinancialApplication, FinancialApplicationCoreData } from "@/types";
import { FinancialApplicationSection } from "@/types/enums";
import { AtomFamily } from "jotai/vanilla/utils/atomFamily";
import threeWayMerge from "@/lib/state/threeWayMerge";
import { hashStringSHA256 } from "@ucl/library";
import { diff } from "jsondiffpatch";
import moment from "moment";
import { sortBy } from "lodash";
import { FinancialApplicationConditionalFlags } from "@/hooks/useFinancialApplicationCoreData";
import FinancialApplicationApiClient from "@/api/financialApplicationApiClient";

const ENABLE_DIFF_LOGGING = false;
const SELECTIVE_UPDATES = true;

export const createFinancialApplicationFormSectionValidationStateAtomFamily = () =>
  atomFamily(
    (_section: FinancialApplicationSection) =>
      atom<FinancialApplicationFormSectionValidationState>({
        validationErrors: atom<Record<string, string[]>>({}),
        awaitingValidation: atom<boolean>(false),
        userEverInitiatedValidationForSection: false,
      }),
    (a, b) => a === b
  );

export interface FinancialApplicationWithHash {
  financialApplication: FinancialApplication;
  hash: string;
}

export interface FinancialApplicationFormSectionValidationState {
  validationErrors: PrimitiveAtom<Record<string, string[]>>;
  awaitingValidation: PrimitiveAtom<boolean>;
  userEverInitiatedValidationForSection: boolean;
}

export interface FinancialApplicationState {
  financialApplication: Atom<FinancialApplication | undefined>;
  hash: Atom<string | undefined>;

  coreData: PrimitiveAtom<FinancialApplicationCoreData | undefined>;
  coreDataLoading: PrimitiveAtom<boolean>;
  coreDataError: PrimitiveAtom<string | null>;
  coreDataFetched: PrimitiveAtom<boolean>;
  disclosures: Atom<Disclosure[]>;
  conditionalFlags: Atom<FinancialApplicationConditionalFlags>;
  validationStateAtomFamily: AtomFamily<
    FinancialApplicationSection,
    PrimitiveAtom<FinancialApplicationFormSectionValidationState>
  >;
  lastSavedFinancialApplicationValue: PrimitiveAtom<FinancialApplication | undefined>;

  // Setters
  setFinancialApplicationFromForm: WritableAtom<null, [FinancialApplication], Promise<boolean>>;
  setFinancialApplicationFromRemote: WritableAtom<
    null,
    [FinancialApplication, FinancialApplication | undefined],
    Promise<void>
  >;
  refreshCoreData: WritableAtom<null, [FinancialApplicationApiClient, boolean], Promise<void>>;

  // Ref-like members
  financialApplicationId: string;
  afterSaveQueue: (() => Promise<any>)[];
  isSaving: boolean;
}

export const financialApplicationAtomFamily = atomFamily(
  (financialApplicationId: string) => {
    const financialApplicationWithHash = atom<FinancialApplicationWithHash | undefined>(undefined);
    const financialApplication = atom(
      (get) => get(financialApplicationWithHash)?.financialApplication
    );
    const hash = atom((get) => get(financialApplicationWithHash)?.hash);

    const coreData = atom<FinancialApplicationCoreData | undefined>(undefined);
    const coreDataLoading = atom<boolean>(false);
    const coreDataError = atom<string | null>(null);
    const coreDataFetched = atom<boolean>(false);
    const validationStateAtomFamily =
      createFinancialApplicationFormSectionValidationStateAtomFamily();
    const lastSavedFinancialApplicationValue = atom<FinancialApplication | undefined>(undefined);

    // Derived atom for disclosures
    const disclosures = atom((get) => {
      const data = get(coreData);
      return sortBy(data?.disclosureAcknowledgements || [], 'createdAt');
    });

    // Derived atom for conditional flags
    const conditionalFlags = atom((get) => {
      const data = get(coreData);

      return {
        // Show account details if any products are selected
        isAccountDetailsEnabled: !!data?.hasPersonalAccountSelected || !!data?.hasBusinessAccountSelected || !!data?.hasIoltaAccountSelected,
  
        // Show joint account owners if user selected "will have joint owners" AND has personal products selected
        isJointAccountOwnersEnabled: !!data?.willHaveJointAccountOwners && !!data?.hasPersonalAccountSelected,
        
        // Show personal account beneficiaries if user selected "will have personal account beneficiaries" AND has personal products selected
        isPersonalAccountBeneficiariesEnabled: !!data?.willHavePersonalAccountBeneficiaries && !!data?.hasPersonalAccountSelected,
        
        // Show organization info if business or IOLTA products are selected
        isOrganizationInfoEnabled: !!data?.hasBusinessAccountSelected || !!data?.hasIoltaAccountSelected,
        
        // Show key people if business or IOLTA products are selected
        isKeyPeopleEnabled: (!!data?.hasBusinessAccountSelected || !!data?.hasIoltaAccountSelected) && !data.isOrganizationSoleProprietorship,
        
        // Show personal acceptable use if personal products are selected
        isPersonalAccountAcceptableUseEnabled: !!data?.hasPersonalAccountSelected,
        
        // Show organization acceptable use if business or IOLTA products are selected
        isOrganizationAcceptableUseEnabled: !!data?.hasBusinessAccountSelected || !!data?.hasIoltaAccountSelected,
        
        // Risk review tab conditional flags
        // Show personal account risk review tab if personal products are selected
        isPersonalAccountRiskReviewEnabled: !!data?.hasPersonalAccountSelected,
        
        // Show law firm organization account risk review tab if business/IOLTA products are selected AND has Offices of Lawyers NAICS code
        isLawFirmOrganizationAccountRiskReviewEnabled: (!!data?.hasBusinessAccountSelected || !!data?.hasIoltaAccountSelected) && !!data?.isOfficesOfLawyersNaicsCode,
        
        // Show non-law firm organization account risk review tab if business/IOLTA products are selected but NOT Offices of Lawyers NAICS code
        isNonLawFirmOrganizationAccountRiskReviewEnabled: (!!data?.hasBusinessAccountSelected || !!data?.hasIoltaAccountSelected) && !data?.isOfficesOfLawyersNaicsCode,
        
        // Show attorney bar number if IOLTA products are selected
        // shouldShowAttorneyBarNumber: !!data?.hasIoltaAccountSelected,
        shouldShowAttorneyBarNumber: false,
        
        // Show business name on debit card if business or IOLTA products are selected
        showBusinessCard: !!data?.hasBusinessAccountSelected || !!data?.hasIoltaAccountSelected,
      };
    });

    const afterSaveQueue: (() => Promise<any>)[] = [];

    const setFinancialApplicationFromForm = atom(
      null,
      async (get, set, update: FinancialApplication): Promise<boolean> => {
        const currentValue = get(financialApplication);
        if (ENABLE_DIFF_LOGGING) {
          const compared = diff(currentValue, update);
          console.debug("local versus update", compared);
        }
        const currentHash = get(hash);
        const newHash = await hashStringSHA256(JSON.stringify(update));

        // Check if the hash is initial
        const isInitialHash = !currentHash; // Example: '' or undefined

        if (currentHash === newHash) {
          return false;
        }

        set(financialApplicationWithHash, {
          financialApplication: {
            ...update,
            validationState: currentValue?.validationState,
          },
          hash: newHash,
        });

        // If the hash is initial,set as no change
        // Leave code logic for readability
        if (isInitialHash) {
          return false;
        }

        // If not initial hash, set as changed
        return true;
      }
    );

    const setFinancialApplicationFromRemote = atom(
      null,
      async (
        get,
        set,
        update: FinancialApplication,
        lastSaved: FinancialApplication | undefined
      ): Promise<void> => {
        if (lastSaved) {
          set(lastSavedFinancialApplicationValue, lastSaved);
        }

        const lastSavedValue = lastSaved ?? get(lastSavedFinancialApplicationValue);
        const currentValue = get(financialApplication);
        const currentHash = get(hash);

        if (update.updatedAt) {
          const updatedAtDateStringWithoutZ = update.updatedAt.endsWith("Z") ? update.updatedAt.slice(0, -1) : update.updatedAt;
          const currentValueUpdatedAtDateStringWithoutZ = currentValue?.updatedAt?.endsWith("Z") ? currentValue.updatedAt.slice(0, -1) : currentValue?.updatedAt;

          const updateMoment = moment(updatedAtDateStringWithoutZ);
          const currentMoment = currentValueUpdatedAtDateStringWithoutZ ? moment(currentValueUpdatedAtDateStringWithoutZ) : null;

          const isUpdatedAtNewer = !currentMoment || updateMoment.isAfter(currentMoment);

          if (!isUpdatedAtNewer) {
            const isSameUpdateTimestamp = currentMoment && updateMoment.isSame(currentMoment);
            
            if (!isSameUpdateTimestamp) {
              console.trace(
                `Received a financial application update that is older than the most current update. Ignoring it. ${updateMoment.format("MM/DD/YYYY HH:mm:ss.SSS")} vs ${currentMoment?.format("MM/DD/YYYY HH:mm:ss.SSS")}`
              );
            }
            return;
          }
        }

        if (ENABLE_DIFF_LOGGING) {
          const localVersusSaved = diff(currentValue, lastSavedValue);
          const localVersusUpdate = diff(currentValue, update);

          console.debug("localVersusSaved", localVersusSaved);
          console.debug("localVersusUpdate", localVersusUpdate);
        }

        if (SELECTIVE_UPDATES) {
          const merged: FinancialApplication = { ...currentValue } as FinancialApplication;
          if (afterSaveQueue.length == 0) {
            merged.digitalBankingInformation = threeWayMerge(currentValue?.digitalBankingInformation, lastSavedValue?.digitalBankingInformation, update.digitalBankingInformation);
            merged.debitCardInformation = threeWayMerge(currentValue?.debitCardInformation, lastSavedValue?.debitCardInformation, update.debitCardInformation);
  
            merged.isAuthorizedSigner = threeWayMerge(currentValue?.isAuthorizedSigner, lastSavedValue?.isAuthorizedSigner, update.isAuthorizedSigner);
            merged.isBeneficialOwner = threeWayMerge(currentValue?.isBeneficialOwner, lastSavedValue?.isBeneficialOwner, update.isBeneficialOwner);
            merged.isControlPerson = threeWayMerge(currentValue?.isControlPerson, lastSavedValue?.isControlPerson, update.isControlPerson);

            merged.organizationInformation = threeWayMerge(currentValue?.organizationInformation, lastSavedValue?.organizationInformation, update.organizationInformation);
          }

          set(financialApplicationWithHash, {
            financialApplication: {
              ...(currentValue?.id ? {} : update), // in case this is the initial update
              ...merged,
              validationState: update.validationState,
              outstandingTasksCount: update.outstandingTasksCount,
              requiredFieldCount: update.requiredFieldCount,
              requiredFieldRemainingCount: update.requiredFieldRemainingCount,
              totalTasksCount: update.totalTasksCount,
              submittedAt: update.submittedAt,
              // financialApplicationDocuments: update.financialApplicationDocuments,
            },
            hash: currentHash ?? "",
          });
        } else {
          const merged = threeWayMerge(
            currentValue,
            lastSavedValue,
            update
          ) as FinancialApplication;

          set(financialApplicationWithHash, {
            financialApplication: {
              ...merged,
              validationState: update.validationState,
            },
            hash: currentHash ?? "",
          });
        }
      }
    );

    const refreshCoreData = atom(
      null,
      async (get, set, apiClient: FinancialApplicationApiClient, forceRefresh: boolean = false) => {
        // Check if we're already loading or if we've already fetched (unless forcing refresh)
        if (get(coreDataLoading) || (!forceRefresh && get(coreDataFetched))) {
          return;
        }
        
        set(coreDataLoading, true);
        set(coreDataError, null);
        
        try {
          const response = await apiClient.getFinancialApplicationCoreData(financialApplicationId);
          set(coreData, response);
          set(coreDataFetched, true);
        } catch (err) {
          const errorMessage = err instanceof Error ? err.message : "Failed to fetch core data";
          set(coreDataError, errorMessage);
          set(coreData, undefined);
        } finally {
          set(coreDataLoading, false);
        }
      }
    );

    return atom<FinancialApplicationState>({
      financialApplication,
      hash,
      coreData,
      coreDataLoading,
      coreDataError,
      coreDataFetched,
      disclosures,
      conditionalFlags,
      validationStateAtomFamily,
      lastSavedFinancialApplicationValue,
      setFinancialApplicationFromForm,
      setFinancialApplicationFromRemote,
      refreshCoreData,
      financialApplicationId,
      afterSaveQueue,
      isSaving: false,
    });
  },
  (a, b) => a.toLowerCase() === b.toLowerCase()
);
